// Mega Project: "The Living World" — May 6 to May 14, 2026
// Source of truth for project ID, role taxonomy, chapter assignments, and board roster.
// Imported by StudentApp.js and AdminApp.js so both surfaces stay in sync.

export const MEGA_PROJECT_ID = 'mega-living-world-2026';
export const MEGA_PROJECT_TITLE = 'The Living World';
export const MEGA_PROJECT_SUBTITLE = 'Scientific Ambassadors Mega Documentary — Chapters 8–10';
export const MEGA_PROJECT_WINDOW = { startDate: '2026-05-06', endDate: '2026-05-14' };

// 6-role taxonomy. Locked — no freeform roles for the mega project.
export const MEGA_ROLE_TAXONOMY = [
  'Lead Researcher',
  'Scriptwriter',
  'Visual Designer',
  'On-Screen Talent',
  'Producer / Continuity',
  'Fact-Checker / TEKS Verifier',
];

// Map mega-project roles → which work modules they can edit.
// Keeps backwards compatibility with the existing canEditModule helper in StudentApp.
export const MEGA_ROLE_MODULE_MAP = {
  research:      ['Lead Researcher', 'Fact-Checker / TEKS Verifier'],
  script:        ['Scriptwriter', 'Fact-Checker / TEKS Verifier'],
  presentation:  ['Visual Designer', 'On-Screen Talent', 'Producer / Continuity'],
};

// Period → chapter lane assignments (revamp — May 6, 2026).
// Ch 8 → P1 (Heredity & Selection)
// Ch 9 → P3 + P4 (split Body Systems into two career pairs)
// Ch 10 → P8 + P9 (split Classifying Life into Microscopic vs Visible kingdoms)
export const PERIOD_CHAPTER_ASSIGNMENTS = {
  '1': {
    chapter: 'Chapter 8',
    chapterTitle: 'Organization, Reproduction & Evolution',
    careerLane: 'Heredity & Selection',
    suggestedCareers: ['Geneticist', 'Selective Breeder'],
  },
  '3': {
    chapter: 'Chapter 9',
    chapterTitle: 'Body Systems — Cardiac & Nervous',
    careerLane: 'Cardiac & Nervous',
    suggestedCareers: ['Cardiologist', 'Neurologist'],
  },
  '4': {
    chapter: 'Chapter 9',
    chapterTitle: 'Body Systems — Immune & Endocrine',
    careerLane: 'Immune & Endocrine',
    suggestedCareers: ['Immunologist', 'Endocrinologist'],
  },
  '8': {
    chapter: 'Chapter 10',
    chapterTitle: 'Classifying Life — Microscopic Life',
    careerLane: 'Microscopic Life',
    suggestedCareers: ['Microbiologist', 'Mycologist'],
  },
  '9': {
    chapter: 'Chapter 10',
    chapterTitle: 'Classifying Life — Visible Life',
    careerLane: 'Visible Life',
    suggestedCareers: ['Botanist', 'Zoologist'],
  },
};

// SAP Board roster (advisory). Flagged on sa_students as boardMember=true.
// Period 8 has no board rep yet — the Project Lead will appoint one Day 1.
export const BOARD_MEMBER_ALIASES = [
  'Magnetic Panda',
  'Neutron Giraffe',
  'Solar Llama',
  'Xenon Emu',
  'Hyper Koala',
  'Chirpy Pasta',
  'Neutron Quetzal',
  'Optical Viper',
  'Luminous Viper',
  'Sonic Wombat',
  'Radiant Hippo',
];

// Helper: get the chapter assignment for a student's period.
export function getChapterAssignment(period) {
  return PERIOD_CHAPTER_ASSIGNMENTS[String(period)] || null;
}

// Brainstorm gate — only the Career Leader for this exact career may edit the brainstorm.
// Everyone else on that career sees it read-only. Students from the OTHER career in the
// same period will never even load the doc (different career-scoped pod doc id).
export function canEditBrainstormForStudent(s) {
  if (!s) return false;
  const myCareer = s.assignedCareer || s.career || '';
  return Boolean(s.careerLeader === true && myCareer && s.careerLed === myCareer);
}

// Section minimums for the auto-progress derivation. Each Research/Script/Presentation
// section has a target character count. When all sections meet target, that module is 100%.
export const SECTION_MINIMUMS = {
  research: {
    careerOverview: 250,
    education:      200,
    dailyTasks:     200,
    careerImpact:   200,
    sources:         80, // citations list — short
  },
  script: {
    hook:             80,
    intro:           120,
    scienceInAction: 250,
    impact:          120,
    closing:          80,
  },
  presentation: {
    slidesUrl: 10, // any non-empty URL/file id counts
    notes:    100,
  },
};

// Order of sections in progress trackers (admin + leadership views).
// 'recording' is admin-only (manual flag, not derived from typed content).
export const PROGRESS_SECTIONS = [
  { key: 'brainstorm', label: 'Brainstorm', auto: true },
  { key: 'research',   label: 'Research',   auto: true },
  { key: 'script',     label: 'Script',     auto: true },
  { key: 'slides',     label: 'Slides',     auto: true },
  { key: 'recording',  label: 'Recording',  auto: false },
];

// Compute brainstorm progress percentage from the current draft state.
// 11 micro-criteria, each worth ~9%:
//   3 careers × (idea + pros + cons) = 9 fields, each ≥ 25 chars → 9 points
//   selectedCareer non-empty → 1 point
//   pitch ≥ 100 chars → 1 point
// Returns integer 0–100.
export function computeBrainstormPct(careers, selectedCareer, pitch) {
  let earned = 0;
  const totalPoints = 11;
  if (Array.isArray(careers)) {
    for (let i = 0; i < 3; i++) {
      const c = careers[i] || {};
      if ((c.idea || '').toString().trim().length  >= 25) earned++;
      if ((c.pros || '').toString().trim().length  >= 25) earned++;
      if ((c.cons || '').toString().trim().length  >= 25) earned++;
    }
  }
  if ((selectedCareer || '').toString().trim().length >= 2) earned++;
  if ((pitch || '').toString().trim().length >= 100) earned++;
  return Math.round((earned / totalPoints) * 100);
}

// Compute slide/presentation progress: was a deck URL or file recorded? + notes.
export function computeSlidesPct(presentationData) {
  if (!presentationData || typeof presentationData !== 'object') return 0;
  let earned = 0;
  const total = 2;
  // Any of fileUrl / fileName / videoFileUrl / videoFileName counts as "deck attached"
  const deckAttached = !!(presentationData.fileUrl || presentationData.fileName
    || presentationData.videoFileUrl || presentationData.videoFileName
    || presentationData.slidesUrl);
  if (deckAttached) earned++;
  // Notes / description ≥ 100 chars
  const notes = (presentationData.notes || presentationData.description || '').toString();
  if (notes.trim().length >= 100) earned++;
  return Math.round((earned / total) * 100);
}

// Compute 0–100 progress for a module given its data doc and a section→min-chars map.
// Returns the integer percent for that module.
export function computeModuleProgress(moduleKey, data) {
  if (!data || typeof data !== 'object') return 0;
  const mins = SECTION_MINIMUMS[moduleKey];
  if (!mins) return 0;
  const sections = Object.keys(mins);
  if (sections.length === 0) return 0;
  let filled = 0;
  sections.forEach(k => {
    const v = (data[k] ?? '').toString();
    if (v.trim().length >= mins[k]) filled++;
  });
  return Math.round((filled / sections.length) * 100);
}

// Helper: is this an active mega-project student doc? (claimed under the current projectId)
export function isCurrentProjectClaim(studentDoc) {
  return studentDoc?.projectId === MEGA_PROJECT_ID && studentDoc?.claimed;
}

// Project calendar — Mon–Thu only (no school Fridays). Last record day = May 18.
// `iso` is the ISO date so the app can highlight "today" deterministically.
// `recording` rows light up amber. `youDo` is per-role guidance.
export const MEGA_CALENDAR = [
  { iso: '2026-05-06', date: 'Wed 5/6',  phase: 'Launch',
    miniLesson: 'Taxonomy + Domain/Kingdom overview',
    work: 'Onboard. Confirm alias. Pick preferred + 2 backup functional roles. Career assigned by Dr. I-D after onboarding.',
    recording: false,
    youDo: {
      'Lead Researcher':              'Get to know your career assignment. List 5 questions you would ask the professional.',
      'Scriptwriter':                 'Watch one career-day-in-the-life clip in your lane. Note its three best lines.',
      'Visual Designer':              'Pin 5 reference images for the visual style of your segment. Save links only.',
      'On-Screen Talent':             'Practice introducing yourself in 15 seconds, three different ways.',
      'Producer / Continuity':        'Confirm your pod knows the calendar. Print the pod\'s call schedule.',
      'Fact-Checker / TEKS Verifier': 'Pull the chapter\'s TEKS standard. Highlight the verbs (e.g., "compare", "explain").',
    },
  },
  { iso: '2026-05-07', date: 'Thu 5/7',  phase: 'Research I',
    miniLesson: 'Bacteria & Archaea',
    work: 'Career + role assignments published. Researchers begin career dossier — facts, three citations, three quotes.',
    recording: false,
    youDo: {
      'Lead Researcher':              'Build the dossier — three sources, three quotes, one TEKS-tie paragraph.',
      'Scriptwriter':                 'Read the dossier as it lands. Sketch a 3-act outline of the segment.',
      'Visual Designer':              'Mood board v1 — color, type, image style. Six tiles max.',
      'On-Screen Talent':             'Memorize the career\'s name, education path, and one signature project.',
      'Producer / Continuity':        'Open the pod\'s shared dashboard. Note any blockers and who has them.',
      'Fact-Checker / TEKS Verifier': 'Read every source the Researcher adds. Flag anything you cannot stand behind.',
    },
  },
  { iso: '2026-05-11', date: 'Mon 5/11', phase: 'Research → Script',
    miniLesson: 'Protists, Fungi, Plants',
    work: 'Research locks at end of class. Scriptwriters draft segment outline; Visual Designers begin mood board.',
    recording: false,
    youDo: {
      'Lead Researcher':              'Lock the dossier. Hand it to your Scriptwriter. No new sources after today.',
      'Scriptwriter':                 'Draft the segment outline — hook, 3-beat body, payoff. ~100 words.',
      'Visual Designer':              'Mood board v2. Begin slide template — title slide, body slide, caption slide.',
      'On-Screen Talent':             'Read the dossier aloud. Time yourself summarizing it in 90 seconds.',
      'Producer / Continuity':        'Confirm props and wardrobe constraints with each role-holder.',
      'Fact-Checker / TEKS Verifier': 'Verify outline claims against dossier. Mark each line ✓ or ?.',
    },
  },
  { iso: '2026-05-12', date: 'Tue 5/12', phase: 'Script Day',
    miniLesson: 'Animals + ecosystem roles',
    work: 'Full script v1. On-Screen Talent reads aloud + times. Visual Designers build slides v1.',
    recording: false,
    youDo: {
      'Lead Researcher':              'Sit with Scriptwriter. Answer fact questions. Add no new content.',
      'Scriptwriter':                 'Script v1 — full text, 360–400 words for 3 minutes. Annotate slide cues.',
      'Visual Designer':              'Slides v1 from the script. 6–9 slides max. Big legible text.',
      'On-Screen Talent':             'Read v1 aloud twice. Mark words you stumble on. Time both reads.',
      'Producer / Continuity':        'Build the call sheet for recording day. Wardrobe note per role.',
      'Fact-Checker / TEKS Verifier': 'Mark every claim in v1. Confirm or query each.',
    },
  },
  { iso: '2026-05-13', date: 'Wed 5/13', phase: 'Polish + Rehearsal',
    miniLesson: 'Cumulative recap',
    work: 'Script v2 with Fact-Checker sign-off. Slides v2. Two timed rehearsals with camera-blocking.',
    recording: false,
    youDo: {
      'Lead Researcher':              'On call for last-minute fact verifications. Otherwise observe rehearsal.',
      'Scriptwriter':                 'Final script v2. Lock language. No more rewrites after Fact-Checker signs.',
      'Visual Designer':              'Slides v2 + B-roll list. Verify every slide reads from six feet away.',
      'On-Screen Talent':             'Two timed rehearsals with slides. Hit the 3-minute window ±15 sec.',
      'Producer / Continuity':        'Final call sheet. Confirm props, wardrobe, and shoot order.',
      'Fact-Checker / TEKS Verifier': 'Sign off the locked script. Attach the TEKS line. This unlocks recording.',
    },
  },
  { iso: '2026-05-14', date: 'Thu 5/14', phase: 'RECORDING — Day 1 (P1, P3, P4)',
    miniLesson: 'Wrap & celebration',
    work: 'Periods 1, 3, and 4 record. Producers manage call sheet, props, continuity.',
    recording: true,
    youDo: {
      'Lead Researcher':              'On set for fact verification on pickups. Otherwise quiet observer.',
      'Scriptwriter':                 'On set with the locked script in hand. No rewrites — only line readings.',
      'Visual Designer':              'Cue slides on set. Confirm B-roll captures match the slide list.',
      'On-Screen Talent':             'Deliver. Two clean takes minimum. Hit your marks. Voice projected.',
      'Producer / Continuity':        'Run the call sheet. You are the only voice between pod and camera.',
      'Fact-Checker / TEKS Verifier': 'On set for any pickup verification. Sign the recording log when done.',
    },
  },
  { iso: '2026-05-18', date: 'Mon 5/18', phase: 'RECORDING — Day 2 (P8, P9 + pickups)',
    miniLesson: 'Project debrief',
    work: 'Periods 8 and 9 record. Pickups for any segment that needs them. Project closes at end of day.',
    recording: true,
    youDo: {
      'Lead Researcher':              'Same as Day 1.',
      'Scriptwriter':                 'Same as Day 1.',
      'Visual Designer':              'Same as Day 1.',
      'On-Screen Talent':             'Deliver. Final takes. Project closes EOD.',
      'Producer / Continuity':        'Final call sheet of the project. Sign the wrap log.',
      'Fact-Checker / TEKS Verifier': 'Final sign-off on every segment recorded today.',
    },
  },
];

// Find today's row in the calendar (or null if today isn't a project day).
// Uses YYYY-MM-DD comparison so it's deterministic regardless of locale/timezone.
export function getTodayCalendarRow(now = new Date()) {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const today = `${yyyy}-${mm}-${dd}`;
  return MEGA_CALENDAR.find(row => row.iso === today) || null;
}

// Find the next upcoming row when today isn't in the calendar.
export function getNextCalendarRow(now = new Date()) {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const today = `${yyyy}-${mm}-${dd}`;
  return MEGA_CALENDAR.find(row => row.iso >= today) || null;
}

// Career Leaders — one per career inside a period. Held in addition to a functional role.
// Pod Leaders — ~8 per period (one per table). Held in addition to a functional role.
// Board Members — project-level liaisons; hold a functional role too.
export function getStudentCareerLeader(s) {
  return Boolean(s?.careerLeader);
}
export function getStudentPodLeader(s) {
  return Boolean(s?.podLeader);
}

// On-screen permission. Defaults to allowed; opt-out flips to false (parent-opt-out).
// Aliases-only on screen for any student where this is false.
export function isOnScreenAllowed(s) {
  return s?.onScreenAllowed !== false;
}

// One pod per class period. Every student in a given period shares this pod name.
// Pod doc IDs are `${MEGA_PROJECT_ID}_${period}_${MEGA_POD_NAME}` so existing
// pod-keyed reads (still using `${period}_${pod}`-shaped IDs) keep working unchanged.
export const MEGA_POD_NAME = 'class';

// Default career slate per period — used when admin has not customized via the
// Career Slate editor. Falls back to the chapter's suggestedCareers list.
export function getDefaultCareerSlate(period) {
  const a = getChapterAssignment(period);
  return a ? [...a.suggestedCareers] : [];
}

// Returns the active career slate for a period, given the live project doc
// (from Firestore at `sa_projects/${MEGA_PROJECT_ID}`). Admin-set list wins;
// otherwise falls back to the default slate.
export function getCareerSlate(period, projectDoc) {
  const fromDoc = projectDoc?.careerSlate?.[String(period)];
  if (Array.isArray(fromDoc) && fromDoc.length) return fromDoc;
  return getDefaultCareerSlate(period);
}
